import { Link } from 'expo-router';
import ArrowRight from 'lucide-react-native/icons/arrow-right';
import ChevronLeft from 'lucide-react-native/icons/chevron-left';
import ChevronRight from 'lucide-react-native/icons/chevron-right';
import { useRef } from 'react';
import { Pressable, StyleSheet, Text } from 'react-native';
import { useTranslation } from 'react-i18next';

import { navHref } from '@/components/riviera/nav-links';
import { TourCard } from '@/components/riviera/tour-card';
import { Fonts, Spacing } from '@/constants/theme';
import { useFilters } from '@/hooks/use-filters';
import { useTheme } from '@/hooks/use-theme';
import { useLocale } from '@todo/core/i18n/use-locale';
import { useTours } from '@todo/core/hooks/use-tours';
import { cities } from '@/lib/events-data';
import { TOURS_INDEX_QUERY, TOURS_INDEX_SEED_KEY } from '@todo/core/list-queries';

import css from './carousel-section.module.css';

/** How many cards a carousel renders — the seeded list holds more than this. */
const CAROUSEL_TOURS_LIMIT = 10;

// Web-only mirror of experiences-carousel.tsx that resolves its responsive
// layout with CSS at first paint instead of a post-mount `isLg` swap. See
// events-carousel.web.tsx / carousel-section.module.css for the rationale.
export function ExperiencesCarousel() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { activeCity } = useFilters();
  const isDefaultView = activeCity === 'all';
  const { tours: allTours, loading, error } = useTours(
    isDefaultView ? TOURS_INDEX_QUERY : { city: activeCity },
    isDefaultView ? TOURS_INDEX_SEED_KEY : undefined,
  );
  const tours = allTours.slice(0, CAROUSEL_TOURS_LIMIT);
  const cityName = cities.find((c) => c.id === activeCity)?.name;
  const heading =
    activeCity !== 'all' && cityName
      ? t('tours.headingCity', { city: cityName })
      : t('tours.headingRegion');

  // Hide the whole section when there's nothing to show (e.g. before the first
  // Viator sync) rather than leaving an empty partner strip on the page.
  if (!loading && !error && tours.length === 0) return null;

  const scrollBy = (dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  return (
    <div className={css.section}>
      <div className={css.heading}>
        <h2 className={css.headingText} style={{ color: theme.text, fontFamily: Fonts?.serif }}>
          {heading}
        </h2>
        {/* "See all" was an anchor with no destination. It now points at the
            tours hub, which is also how the homepage gets a crawlable link
            into that listing. */}
        {/* style must be a plain object, not the usual `({pressed}) => [...]`
            function: <Link asChild> merges props into this Pressable via
            expo-router's Slot (a thin wrapper around @radix-ui/react-slot),
            whose prop merge spreads style objects together — spreading a
            function copies none of its array contents, so a function style
            here silently loses flexDirection/alignItems/gap and the icon
            wraps onto its own line. Pressed feedback goes through `children`
            instead, which isn't touched by that merge. */}
        <Link href={navHref('/[lang]/tours', locale)} asChild>
          <Pressable accessibilityRole="link" style={styles.seeAll}>
            {({ pressed }) => (
              <>
                <Text
                  style={[
                    styles.seeAllText,
                    { color: theme.primary },
                    pressed && { opacity: 0.6 },
                  ]}>
                  {t('common.seeAll')}
                </Text>
                <ArrowRight
                  size={16}
                  color={theme.primary}
                  style={pressed && { opacity: 0.6 }}
                />
              </>
            )}
          </Pressable>
        </Link>
      </div>
      <div className={css.carouselWrap}>
        <div ref={scrollRef} className={css.scroller}>
          {loading ? (
            <Text style={{ color: theme.mutedForeground, paddingHorizontal: Spacing.three }}>
              {t('tours.loading')}
            </Text>
          ) : error ? (
            <Text style={{ color: theme.destructive, paddingHorizontal: Spacing.three }}>
              {error}
            </Text>
          ) : (
            tours.map((tour) => <TourCard key={tour.id} tour={tour} />)
          )}
        </div>
        <div className={css.arrows}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('a11y.previous')}
            onPress={() => scrollBy(-1)}
            style={({ pressed }) => [
              styles.navBtn,
              styles.navBtnLeft,
              { backgroundColor: theme.card, borderColor: theme.border },
              pressed && { opacity: 0.8 },
            ]}>
            <ChevronLeft size={20} color={theme.text} />
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('a11y.next')}
            onPress={() => scrollBy(1)}
            style={({ pressed }) => [
              styles.navBtn,
              styles.navBtnRight,
              { backgroundColor: theme.card, borderColor: theme.border },
              pressed && { opacity: 0.8 },
            ]}>
            <ChevronRight size={20} color={theme.text} />
          </Pressable>
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  seeAll: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  seeAllText: {
    fontSize: 13,
    fontWeight: '600',
  },
  navBtn: {
    position: 'absolute',
    top: '50%',
    marginTop: -18,
    width: 36,
    height: 36,
    borderRadius: 999,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navBtnLeft: {
    left: -12,
  },
  navBtnRight: {
    right: -12,
  },
});
